/**
 * CreateCustomCreditNoteModal
 * Modal for creating custom credit notes (standalone, not linked to any invoice)
 * Based on CreateCustomInvoiceModal
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  EyeIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createCustomCreditNote, validateCreditNote, previewCreditNotePdf, downloadCreditNotePdf } from '../../api/facturation.api';
import type { BelgianVatRate, CreditNote, ContactSearchResult } from '../../types/facturation.types';
import { validateEmail, normalizeVatNumberForPrefill } from '../../utils/validation';
import { calculateLineTotals, calculateLineTotalsFromTvac } from '../../utils/currency';
import { COUNTRY_OPTIONS, toCountryCode } from '../../constants/countries.constants';
import { useContactSearch } from '../../hooks/useDevis';
import { useTranslation } from '@/i18n/useTranslation';
import { PdfPreviewModal } from '../PdfPreviewModal/PdfPreviewModal';
import '../../facturation.css';

interface PrefillCustomer {
  name?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  countryCode?: string;
  email?: string;
  vatNumber?: string;
}

interface CreateCustomCreditNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Optional prefill data from affaire billing contact */
  prefillCustomer?: PrefillCustomer;
  /** Optional: link credit note to an affaire (displays affaire info in PDF) */
  affaireId?: string;
}

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

// Language options
const languages = [
  { code: 'fr', label: 'Francais' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

// VAT rate options
const vatRateOptions: { rate: BelgianVatRate; label: string }[] = [
  { rate: 21, label: '21%' },
  { rate: 12, label: '12%' },
  { rate: 6, label: '6%' },
  { rate: 0, label: '0%' },
];

// Line item for the form
interface LineItemForm {
  id: string;
  description: string;
  quantity: number;
  unitPriceHtva: number;
  unitPriceTvac: number;
  tvaRate: BelgianVatRate;
}

// Customer form
interface CustomerForm {
  name: string;
  address: string;
  postalCode: string;
  city: string;
  countryCode: string;
  email: string;
  vatNumber: string;
}

type InvoiceLanguage = 'fr' | 'nl' | 'de' | 'en';

// Generate unique id for line items
const generateId = () => Math.random().toString(36).substring(2, 9);

export const CreateCustomCreditNoteModal: React.FC<CreateCustomCreditNoteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  prefillCustomer,
  affaireId,
}) => {
  const { t: _t } = useTranslation();
  const queryClient = useQueryClient();

  // Helper to get initial customer state (with optional prefill)
  const getInitialCustomer = useCallback((): CustomerForm => ({
    name: prefillCustomer?.name || '',
    address: prefillCustomer?.address || '',
    postalCode: prefillCustomer?.postalCode || '',
    city: prefillCustomer?.city || '',
    countryCode: prefillCustomer?.countryCode || 'BE',
    email: prefillCustomer?.email || '',
    vatNumber: prefillCustomer?.vatNumber || '',
  }), [prefillCustomer]);

  // Step state
  const [step, setStep] = useState<'customer' | 'lines' | 'generating' | 'generated' | 'validating' | 'success' | 'error'>('customer');

  // Customer form
  const [customer, setCustomer] = useState<CustomerForm>(getInitialCustomer);

  // Existing client search (prefill from fm_contacts, same endpoint as devis)
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientResults, setShowClientResults] = useState(false);
  const { data: clientResults } = useContactSearch(clientSearchTerm);

  // Reason (required for credit notes)
  const [reason, setReason] = useState('');

  // Title (optional)
  const [title, setTitle] = useState('');

  // Credit note date (defaults to today)
  const [creditNoteDate, setCreditNoteDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Language
  const [selectedLanguage, setSelectedLanguage] = useState<InvoiceLanguage>('fr');

  // Line items
  const [lines, setLines] = useState<LineItemForm[]>([
    { id: generateId(), description: '', quantity: 1, unitPriceHtva: 0, unitPriceTvac: 0, tvaRate: 21 },
  ]);

  // Generated credit note
  const [generatedCreditNote, setGeneratedCreditNote] = useState<CreditNote | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [_isDownloading, setIsDownloading] = useState(false);
  const [sendEmailOnValidate, setSendEmailOnValidate] = useState(true);

  // Errors
  const [errorMessage, setErrorMessage] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Mutations
  const createMutation = useMutation({
    mutationFn: createCustomCreditNote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creditNotes'] });
    },
  });

  const validateMutation = useMutation({
    mutationFn: ({ id, sendEmail }: { id: number; sendEmail: boolean }) =>
      validateCreditNote(id, sendEmail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creditNotes'] });
    },
  });

  // Prefill customer form from a selected existing client (fields stay editable)
  const handleSelectContact = useCallback((contact: ContactSearchResult) => {
    setCustomer({
      name: contact.name || '',
      address: contact.address_street || '',
      postalCode: contact.postal_code || '',
      city: contact.city || '',
      countryCode: toCountryCode(contact.pays),
      email: contact.email || '',
      vatNumber: normalizeVatNumberForPrefill(contact.vat_number),
    });
    setEmailError(null);
    setErrorMessage('');
    setClientSearchTerm(contact.name || '');
    setShowClientResults(false);
  }, []);

  // Reset form when modal opens/closes
  const handleClose = useCallback(() => {
    setStep('customer');
    setCustomer({ name: '', address: '', postalCode: '', city: '', countryCode: 'BE', email: '', vatNumber: '' });
    setClientSearchTerm('');
    setShowClientResults(false);
    setReason('');
    setTitle('');
    setCreditNoteDate(new Date().toISOString().split('T')[0]);
    setSelectedLanguage('fr');
    setLines([{ id: generateId(), description: '', quantity: 1, unitPriceHtva: 0, unitPriceTvac: 0, tvaRate: 21 }]);
    setGeneratedCreditNote(null);
    setShowPdfPreview(false);
    setErrorMessage('');
    setEmailError(null);
    setSendEmailOnValidate(true);
    onClose();
  }, [onClose]);

  // Calculate totals
  const totals = useMemo(() => {
    let totalHtva = 0;
    let totalTva = 0;
    let totalTvac = 0;

    lines.forEach((line) => {
      if (line.description.trim() && line.quantity > 0 && (line.unitPriceHtva > 0 || line.unitPriceTvac > 0)) {
        // TVAC entered → TVAC is the source of truth; otherwise HTVA drives it.
        const t = line.unitPriceTvac > 0
          ? calculateLineTotalsFromTvac(line.quantity, line.unitPriceTvac, line.tvaRate)
          : calculateLineTotals(line.quantity, line.unitPriceHtva, line.tvaRate);
        totalHtva += t.lineHtva;
        totalTva += t.lineTva;
        totalTvac += t.lineTvac;
      }
    });

    return {
      totalHtva: Math.round(totalHtva * 100) / 100,
      totalTva: Math.round(totalTva * 100) / 100,
      totalTvac: Math.round(totalTvac * 100) / 100,
    };
  }, [lines]);

  // Validate customer step
  const validateCustomerStep = (): boolean => {
    if (!customer.name.trim()) {
      setErrorMessage('Le nom du client est requis');
      return false;
    }
    if (!customer.email?.trim()) {
      setEmailError('L\'email du client est requis');
      return false;
    }
    const validation = validateEmail(customer.email.trim(), false);
    if (!validation.valid) {
      setEmailError(validation.error || 'Email invalide');
      return false;
    }
    if (!reason.trim()) {
      setErrorMessage('La raison de la note de credit est requise');
      return false;
    }
    setErrorMessage('');
    setEmailError(null);
    return true;
  };

  // Validate lines step
  const validateLinesStep = (): boolean => {
    const validLines = lines.filter(
      (line) => line.description.trim() && line.quantity > 0 && (line.unitPriceHtva > 0 || line.unitPriceTvac > 0)
    );
    if (validLines.length === 0) {
      setErrorMessage('Ajoutez au moins une ligne valide');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  // Handle next step
  const handleNext = () => {
    if (step === 'customer') {
      if (validateCustomerStep()) {
        setStep('lines');
      }
    } else if (step === 'lines') {
      if (validateLinesStep()) {
        handleGenerate();
      }
    }
  };

  // Handle back
  const handleBack = () => {
    setErrorMessage('');
    if (step === 'lines') {
      setStep('customer');
    } else if (step === 'generated') {
      setStep('lines');
    }
  };

  // Generate credit note
  const handleGenerate = async () => {
    setStep('generating');
    setErrorMessage('');

    try {
      const validLines = lines
        .filter((line) => line.description.trim() && line.quantity > 0 && (line.unitPriceHtva > 0 || line.unitPriceTvac > 0))
        .map((line) => ({
          description: line.description.trim(),
          quantity: line.quantity,
          unitPriceHtva: line.unitPriceHtva,
          // Send TVAC when the user entered it → backend uses it as source of truth.
          unitPriceTvac: line.unitPriceTvac > 0 ? line.unitPriceTvac : undefined,
          tvaRate: line.tvaRate,
        }));

      const result = await createMutation.mutateAsync({
        reason: reason.trim(),
        title: title.trim() || undefined,
        customer: {
          name: customer.name.trim(),
          address: customer.address?.trim() || undefined,
          postalCode: customer.postalCode?.trim() || undefined,
          city: customer.city?.trim() || undefined,
          countryCode: customer.countryCode?.trim() || undefined,
          email: customer.email.trim(),
          vatNumber: customer.vatNumber?.trim() || undefined,
        },
        language: selectedLanguage,
        lines: validLines,
        affaireId: affaireId || undefined,
        creditNoteDate: creditNoteDate || undefined,
      });

      setGeneratedCreditNote(result);
      setStep('generated');
    } catch (error) {
      console.error('Failed to generate credit note:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Erreur lors de la generation');
      setStep('error');
    }
  };

  // Validate and send credit note
  const handleValidate = async () => {
    if (!generatedCreditNote) return;

    setStep('validating');
    setErrorMessage('');

    try {
      const shouldSendEmail = sendEmailOnValidate && !!customer.email?.trim();
      await validateMutation.mutateAsync({
        id: generatedCreditNote.id,
        sendEmail: shouldSendEmail,
      });

      setStep('success');
      onSuccess?.();
    } catch (error) {
      console.error('Failed to validate credit note:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Erreur lors de la validation');
      setStep('error');
    }
  };

  // Download PDF
  const handleDownloadPdf = async () => {
    if (!generatedCreditNote) return;

    setIsDownloading(true);
    try {
      const blob = generatedCreditNote.status === 'DRAFT'
        ? await previewCreditNotePdf(generatedCreditNote.id)
        : await downloadCreditNotePdf(generatedCreditNote.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `note_credit_${generatedCreditNote.credit_note_number || 'brouillon'}.pdf`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Get step title
  const getStepTitle = () => {
    switch (step) {
      case 'customer':
        return 'Client et motif';
      case 'lines':
        return 'Lignes';
      case 'generating':
        return 'Generation en cours...';
      case 'generated':
        return 'Verifier la note de credit';
      case 'validating':
        return 'Envoi en cours...';
      case 'success':
        return 'Note de credit envoyee';
      case 'error':
        return 'Erreur';
      default:
        return 'Note de credit personnalisee';
    }
  };

  // Get step subtitle
  const getStepSubtitle = () => {
    switch (step) {
      case 'customer':
        return 'Etape 1/2 - Informations client et motif';
      case 'lines':
        return 'Etape 2/2 - Lignes de credit';
      case 'generated':
        return generatedCreditNote?.credit_note_number || 'Brouillon';
      default:
        return 'Note de credit sans facture';
    }
  };

  // Add line
  const addLine = () => {
    setLines([...lines, { id: generateId(), description: '', quantity: 1, unitPriceHtva: 0, unitPriceTvac: 0, tvaRate: 21 }]);
  };

  // Remove line
  const removeLine = (id: string) => {
    if (lines.length > 1) {
      setLines(lines.filter((line) => line.id !== id));
    }
  };

  // Update line
  const updateLine = (id: string, field: keyof LineItemForm, value: string | number) => {
    setLines(
      lines.map((line) =>
        line.id === id ? { ...line, [field]: value } : line
      )
    );
  };

  // Bidirectional HTVA/TVAC sync per line (mirrors the invoice composer).
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const updateLineHtva = (id: string, htva: number) => {
    setLines(lines.map((line) =>
      line.id === id
        ? { ...line, unitPriceHtva: htva, unitPriceTvac: round2(htva * (1 + line.tvaRate / 100)) }
        : line
    ));
  };
  const updateLineTvac = (id: string, tvac: number) => {
    setLines(lines.map((line) =>
      // Keep HTVA full precision so the TVAC round-trips exactly.
      line.id === id
        ? { ...line, unitPriceTvac: tvac, unitPriceHtva: tvac / (1 + line.tvaRate / 100) }
        : line
    ));
  };
  const updateLineRate = (id: string, rate: BelgianVatRate) => {
    setLines(lines.map((line) =>
      line.id === id
        ? { ...line, tvaRate: rate, unitPriceTvac: round2(line.unitPriceHtva * (1 + rate / 100)) }
        : line
    ));
  };

  return (
    <>
      <Transition show={isOpen} as={React.Fragment}>
        <Dialog
          onClose={() => {
            if (step !== 'generating' && step !== 'validating' && !showPdfPreview) {
              handleClose();
            }
          }}
          className="relative z-50"
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform rounded-2xl bg-white shadow-xl transition-all flex flex-col max-h-[90vh]">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <DocumentTextIcon className="h-6 w-6 text-primary" />
                      <div>
                        <Dialog.Title className="text-lg font-semibold text-gray-900">
                          {getStepTitle()}
                        </Dialog.Title>
                        <p className="text-sm text-gray-500">{getStepSubtitle()}</p>
                      </div>
                    </div>
                    <button
                      onClick={handleClose}
                      className="facturation-btn-modal-close"
                      disabled={step === 'generating' || step === 'validating'}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-6 overflow-y-auto flex-1">
                    {/* Error message */}
                    {errorMessage && step !== 'error' && (
                      <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                        {errorMessage}
                      </div>
                    )}

                    {/* Step: Customer & Reason */}
                    {step === 'customer' && (
                      <div className="space-y-4">
                        {/* Existing client search (prefill) */}
                        <div className="relative p-3 bg-gray-50 rounded-lg">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Client existant
                          </label>
                          <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="text"
                              value={clientSearchTerm}
                              onChange={(e) => {
                                setClientSearchTerm(e.target.value);
                                setShowClientResults(true);
                              }}
                              onFocus={() => setShowClientResults(true)}
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                              placeholder="Rechercher par nom, TVA ou email..."
                              aria-label="Rechercher un client existant pour pre-remplir le formulaire"
                            />
                          </div>
                          {showClientResults && clientResults && clientResults.length > 0 && (
                            <div className="absolute z-10 left-3 right-3 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {clientResults.map((contact) => (
                                <button
                                  key={contact.contact_id}
                                  type="button"
                                  onClick={() => handleSelectContact(contact)}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b last:border-b-0"
                                >
                                  <div className="font-medium text-gray-900 text-sm">{contact.name}</div>
                                  {(contact.address || contact.vat_number) && (
                                    <div className="text-xs text-gray-500">
                                      {[contact.address, contact.vat_number].filter(Boolean).join(' — ')}
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                          <p className="mt-1 text-xs text-gray-500">
                            Selectionner un client pre-remplit les champs ci-dessous (modifiables).
                          </p>
                        </div>

                        {/* Customer name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nom du client <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={customer.name}
                            onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                            placeholder="Nom complet ou societe"
                          />
                        </div>

                        {/* Address - street */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Adresse (rue et numero)
                          </label>
                          <textarea
                            value={customer.address || ''}
                            onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                            rows={2}
                            placeholder="Rue, numero"
                          />
                        </div>

                        {/* Address - postal code / city / country */}
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Code postal
                            </label>
                            <input
                              type="text"
                              value={customer.postalCode || ''}
                              onChange={(e) => setCustomer({ ...customer, postalCode: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                              placeholder="1000"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Ville
                            </label>
                            <input
                              type="text"
                              value={customer.city || ''}
                              onChange={(e) => setCustomer({ ...customer, city: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                              placeholder="Bruxelles"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Pays
                            </label>
                            <select
                              value={customer.countryCode || 'BE'}
                              onChange={(e) => setCustomer({ ...customer, countryCode: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                            >
                              {COUNTRY_OPTIONS.map((c) => (
                                <option key={c.code} value={c.code}>{c.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          {/* Email */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Email <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              value={customer.email || ''}
                              onChange={(e) => {
                                setCustomer({ ...customer, email: e.target.value });
                                setEmailError(null);
                              }}
                              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary ${
                                emailError ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="email@exemple.com"
                              required
                            />
                            {emailError && <p className="mt-1 text-xs text-red-600">{emailError}</p>}
                          </div>

                          {/* VAT number */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Numero TVA
                            </label>
                            <input
                              type="text"
                              value={customer.vatNumber || ''}
                              onChange={(e) => setCustomer({ ...customer, vatNumber: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                              placeholder="BE0123456789"
                            />
                          </div>
                        </div>

                        {/* Reason */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Motif de la note de credit <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                            rows={2}
                            placeholder="Raison de la note de credit..."
                            required
                          />
                        </div>

                        {/* Title (optional) */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Titre (optionnel)
                          </label>
                          <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                            placeholder="Titre de la note de credit"
                          />
                        </div>

                        {/* Credit note date */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date de la note de credit
                          </label>
                          <input
                            type="date"
                            value={creditNoteDate}
                            onChange={(e) => setCreditNoteDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            La date determine l'annee du numero (ex: CN-2025-XXXXX)
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Step: Lines */}
                    {step === 'lines' && (
                      <div className="space-y-4">
                        {/* Lines */}
                        <div className="space-y-3">
                          {lines.map((line, _index) => (
                            <div key={line.id} className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-start gap-3">
                                <div className="flex-1 space-y-2">
                                  <input
                                    type="text"
                                    value={line.description}
                                    onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    placeholder="Description"
                                  />
                                  <div className="grid grid-cols-4 gap-2">
                                    <div>
                                      <label className="block text-xs text-gray-500 mb-1">Quantite</label>
                                      <input
                                        type="number"
                                        min="1"
                                        value={line.quantity}
                                        onChange={(e) => updateLine(line.id, 'quantity', parseInt(e.target.value) || 1)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-500 mb-1">Prix HTVA</label>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={line.unitPriceHtva || ''}
                                        onChange={(e) => updateLineHtva(line.id, parseFloat(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-500 mb-1">Prix TVAC</label>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={line.unitPriceTvac || ''}
                                        onChange={(e) => updateLineTvac(line.id, parseFloat(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-500 mb-1">TVA</label>
                                      <select
                                        value={line.tvaRate}
                                        onChange={(e) => updateLineRate(line.id, parseInt(e.target.value) as BelgianVatRate)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                      >
                                        {vatRateOptions.map((option) => (
                                          <option key={option.rate} value={option.rate}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => removeLine(line.id)}
                                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                                  disabled={lines.length === 1}
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Add line button */}
                        <button
                          onClick={addLine}
                          className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
                        >
                          <PlusIcon className="h-4 w-4" />
                          Ajouter une ligne
                        </button>

                        {/* Totals */}
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Total HTVA:</span>
                              <span>{formatCurrency(totals.totalHtva)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">TVA:</span>
                              <span>{formatCurrency(totals.totalTva)}</span>
                            </div>
                            <div className="flex justify-between font-semibold border-t pt-2">
                              <span>Total TVAC:</span>
                              <span className="text-primary">{formatCurrency(totals.totalTvac)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step: Generating */}
                    {step === 'generating' && (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-4 text-gray-600">Generation de la note de credit...</p>
                      </div>
                    )}

                    {/* Step: Generated (draft preview) */}
                    {step === 'generated' && generatedCreditNote && (
                      <div className="space-y-6">
                        {/* Draft warning */}
                        <div className="fact-draft-warning">
                          <p>
                            <strong>Brouillon</strong> - Le numero sera attribue lors de la validation.
                          </p>
                        </div>

                        <div className="bg-green-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                            <div>
                              <h3 className="font-medium text-green-900">Brouillon cree</h3>
                              <p className="text-sm text-green-700 mt-1">
                                Cliquez sur "Valider" pour attribuer un numero et envoyer a Falco.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Summary */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Resume</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Numero:</span>
                              <span className="font-mono font-medium">{generatedCreditNote.credit_note_number || 'Brouillon'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Client:</span>
                              <span>{generatedCreditNote.billing_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Motif:</span>
                              <span className="truncate max-w-[200px]">{generatedCreditNote.reason}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Total HTVA:</span>
                              <span>{formatCurrency(Number(generatedCreditNote.total_htva))}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">TVA:</span>
                              <span>{formatCurrency(Number(generatedCreditNote.total_tva))}</span>
                            </div>
                            <div className="flex justify-between font-medium border-t pt-2">
                              <span>Total TVAC:</span>
                              <span className="text-primary">{formatCurrency(Number(generatedCreditNote.total_tvac))}</span>
                            </div>
                          </div>
                        </div>

                        {/* Email option */}
                        {customer.email && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={sendEmailOnValidate}
                                onChange={(e) => setSendEmailOnValidate(e.target.checked)}
                                className="fact-email-checkbox"
                              />
                              <span className="text-sm text-gray-700">
                                Envoyer la note de credit par email au client
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1 ml-7">
                              Email: {customer.email}
                            </p>
                          </div>
                        )}

                        {/* Preview button */}
                        <div className="flex justify-center">
                          <button
                            onClick={() => setShowPdfPreview(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors"
                          >
                            <EyeIcon className="h-5 w-5" />
                            Previsualiser le brouillon
                          </button>
                        </div>

                        {/* Next step info */}
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <PaperAirplaneIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                            <div>
                              <h3 className="font-medium text-blue-900">Prochaine etape</h3>
                              <p className="text-sm text-blue-700 mt-1">
                                Verifiez le PDF puis cliquez sur "Valider et envoyer" pour transmettre a Falco.
                                {customer.email && sendEmailOnValidate && (
                                  <span className="block mt-1">
                                    La note de credit sera egalement envoyee par email a <span className="font-medium">{customer.email}</span>.
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step: Validating */}
                    {step === 'validating' && (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-4 text-gray-600">Validation et envoi en cours...</p>
                      </div>
                    )}

                    {/* Step: Success */}
                    {step === 'success' && (
                      <div className="text-center py-8">
                        <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto" />
                        <h3 className="mt-4 text-lg font-semibold text-gray-900">Note de credit envoyee</h3>
                        <p className="mt-2 text-gray-600">
                          La note de credit a ete validee et envoyee a Falco.
                        </p>
                      </div>
                    )}

                    {/* Step: Error */}
                    {step === 'error' && (
                      <div className="text-center py-8">
                        <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto" />
                        <h3 className="mt-4 text-lg font-semibold text-gray-900">Erreur</h3>
                        <p className="mt-2 text-red-600">{errorMessage}</p>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
                    {/* Language selection - only show in first 2 steps */}
                    {['customer', 'lines'].includes(step) && (
                      <div className="flex items-center gap-1">
                        {languages.map((lang) => (
                          <button
                            key={lang.code}
                            type="button"
                            onClick={() => setSelectedLanguage(lang.code as InvoiceLanguage)}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                              selectedLanguage === lang.code
                                ? 'bg-[#ef5343] text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {lang.code.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    )}
                    {['generating', 'generated', 'validating', 'success', 'error'].includes(step) && <div />}

                    {/* Action buttons */}
                    <div className="flex gap-3">
                      {step === 'customer' && (
                        <>
                          <button onClick={handleClose} className="facturation-btn-modal-cancel">
                            Annuler
                          </button>
                          <button onClick={handleNext} className="facturation-btn-modal-primary">
                            Suivant
                          </button>
                        </>
                      )}
                      {step === 'lines' && (
                        <>
                          <button onClick={handleBack} className="facturation-btn-modal-cancel">
                            Retour
                          </button>
                          <button onClick={handleNext} className="facturation-btn-modal-primary">
                            Generer la note de credit
                          </button>
                        </>
                      )}
                      {step === 'generated' && (
                        <>
                          <button onClick={handleClose} className="facturation-btn-modal-cancel">
                            Fermer
                          </button>
                          <button
                            onClick={handleValidate}
                            className="facturation-btn-modal-primary inline-flex items-center gap-2"
                          >
                            <PaperAirplaneIcon className="h-4 w-4" />
                            Valider et envoyer a Falco
                          </button>
                        </>
                      )}
                      {(step === 'success' || step === 'error') && (
                        <button onClick={handleClose} className="facturation-btn-modal-primary">
                          Fermer
                        </button>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* PDF Preview Modal */}
      {showPdfPreview && generatedCreditNote && (
        <PdfPreviewModal
          document={{
            type: 'creditNote',
            id: generatedCreditNote.id,
            number: generatedCreditNote.credit_note_number || 'Brouillon',
            date: generatedCreditNote.credit_note_date || generatedCreditNote.created_at,
            clientName: generatedCreditNote.billing_name || '-',
            amount: Number(generatedCreditNote.total_tvac),
            status: generatedCreditNote.status as 'DRAFT' | 'GENERATED' | 'SENT' | 'PAID' | 'CANCELLED',
            hasPdf: true,
          }}
          onClose={() => setShowPdfPreview(false)}
          onDownload={handleDownloadPdf}
          previewMode={true}
          onBack={() => setShowPdfPreview(false)}
          backLabel="Retour a la validation"
        />
      )}
    </>
  );
};

export default CreateCustomCreditNoteModal;
